import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api'
import { fmtDate, type PageMeta } from '../lib/types'
import { StatusBadge } from '../components/StatusBadge'

type LoginEvent = {
  id: string
  email?: string | null
  success: boolean
  failure_reason?: string | null
  ip?: string | null
  user_agent?: string | null
  created_at?: string | null
}

export function LoginEventsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['login-events'],
    queryFn: () => api<{ items: LoginEvent[]; meta: PageMeta }>('/admin/login-events?page_size=100'),
  })
  return (
    <div>
      <h1 className="page-title">Журнал входов</h1>
      <p className="page-sub">Успешные и неуспешные попытки входа staff</p>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Время</th>
              <th>Email</th>
              <th>Результат</th>
              <th>Причина</th>
              <th>IP</th>
              <th>User-Agent</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6}>Загрузка…</td></tr>}
            {data?.items.map((e) => (
              <tr key={e.id}>
                <td>{fmtDate(e.created_at)}</td>
                <td>{e.email || '—'}</td>
                <td>
                  {e.success
                    ? <StatusBadge value="approved" label="OK" />
                    : <StatusBadge value="rejected" label="Fail" />}
                </td>
                <td>{e.failure_reason || '—'}</td>
                <td>{e.ip || '—'}</td>
                <td style={{ maxWidth: 240, whiteSpace: 'normal' }}>{e.user_agent || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function SecurityPage() {
  const qc = useQueryClient()
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState<string[] | null>(null)
  const { data: setup, isLoading } = useQuery({
    queryKey: ['2fa-setup'],
    queryFn: () => api<{ secret: string; otpauth_url: string; enabled: boolean }>('/admin/security/2fa/setup'),
  })
  const enable = useMutation({
    mutationFn: () => api<{ ok: boolean; recovery_codes: string[] }>('/admin/security/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
    onSuccess: (r) => {
      setRecovery(r.recovery_codes)
      setCode('')
      qc.invalidateQueries({ queryKey: ['2fa-setup'] })
    },
  })
  const disable = useMutation({
    mutationFn: () => api('/admin/security/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
    onSuccess: () => {
      setCode('')
      setRecovery(null)
      qc.invalidateQueries({ queryKey: ['2fa-setup'] })
    },
  })
  const revoke = useMutation({
    mutationFn: () => api('/admin/sessions/revoke-all', { method: 'POST', body: '{}' }),
  })

  return (
    <div className="stack">
      <div>
        <h1 className="page-title">Безопасность</h1>
        <p className="page-sub">2FA (TOTP) и сессии сотрудника</p>
      </div>
      <div className="card stack">
        <h3 style={{ margin: 0 }}>Двухфакторная аутентификация</h3>
        {isLoading && <p className="muted">Загрузка…</p>}
        {setup && (
          <>
            <div>
              Статус:{' '}
              {setup.enabled
                ? <StatusBadge value="approved" label="Включена" />
                : <StatusBadge value="pending" label="Выключена" />}
            </div>
            {!setup.enabled && (
              <div className="stack">
                <p className="muted">Добавьте секрет в Google Authenticator / 1Password:</p>
                <code style={{ wordBreak: 'break-all' }}>{setup.secret}</code>
                <a href={setup.otpauth_url}>Открыть otpauth://</a>
              </div>
            )}
            <div className="field">
              <label>Код подтверждения</label>
              <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 цифр" />
            </div>
            <div className="row">
              {!setup.enabled
                ? <button className="btn btn-primary" disabled={!code.trim()} onClick={() => enable.mutate()}>Включить 2FA</button>
                : <button className="btn btn-danger" disabled={!code.trim()} onClick={() => disable.mutate()}>Отключить 2FA</button>}
            </div>
            {recovery && (
              <div className="error" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#0b7a52' }}>
                Сохраните recovery-коды (показываются один раз):
                <ul>{recovery.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
              </div>
            )}
          </>
        )}
      </div>
      <div className="card stack">
        <h3 style={{ margin: 0 }}>Сессии</h3>
        <p className="muted">Отозвать все refresh-токены текущего сотрудника</p>
        <button className="btn btn-danger" onClick={() => revoke.mutate()}>
          {revoke.isSuccess ? `Отозвано: ${(revoke.data as { revoked?: number })?.revoked ?? 'ok'}` : 'Отозвать все сессии'}
        </button>
      </div>
    </div>
  )
}
